// components/ImageUpload.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Grid,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { CloudUpload, Delete, Image, Search, Close, OpenInNew, LocalPharmacy } from '@mui/icons-material';
import { API_URL } from '../services/appData';
import api from '../services/api';

// Image sources for the search
const IMAGE_SOURCES = {
  AUTO: 'auto',
  PIXABAY: 'pixabay',
  UNSPLASH: 'unsplash',
  WIKIMEDIA: 'wikimedia',
  FLICKR: 'flickr'
};

// Common medication names and their image search terms
const MEDICATION_SEARCH_TERMS = {
  'panadol': ['panadol tablet', 'panadol package', 'panadol extra', 'paracetamol'],
  'بنادول': ['بنادول', 'بنادول اكسترا', 'علبة بنادول'],
  'voltaren': ['voltaren gel', 'voltaren tablet', 'diclofenac'],
  'فولتارين': ['فولتارين', 'جل فولتارين'],
  'augmentin': ['augmentin tablet', 'amoxicillin clavulanate'],
  'أوجمنتين': ['أوجمنتين', 'علبة أوجمنتين']
};

const ImageUpload = ({ value, onChange, error, helperText, productName, ...props }) => {
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState(productName || '');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchSource, setSearchSource] = useState(IMAGE_SOURCES.AUTO);
  const fileInputRef = useRef(null);

  // Generate smart search suggestions based on product name
  const generateSearchSuggestions = (productName) => {
    if (!productName) return [];
    
    const nameLower = productName.toLowerCase();
    const suggestions = new Set();
    
    // Add the original product name
    suggestions.add(productName);
    
    // Add Arabic version if the name is in English
    if (/[a-z]/i.test(productName)) {
      suggestions.add(`علبة ${productName}`);
      suggestions.add(`عبوة ${productName}`);
    }
    
    // Add package/box terms
    suggestions.add(`${productName} package`);
    suggestions.add(`${productName} box`);
    suggestions.add(`${productName} tablet`);
    suggestions.add(`${productName} medicine`);
    
    // Check for known medications
    Object.keys(MEDICATION_SEARCH_TERMS).forEach(medKey => {
      if (nameLower.includes(medKey)) {
        MEDICATION_SEARCH_TERMS[medKey].forEach(term => suggestions.add(term));
      }
    });
    
    return Array.from(suggestions).slice(0, 6);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Upload to server
      const imageUrl = await uploadImage(file);
      onChange(imageUrl);

    } catch (error) {
      console.error('Upload failed:', error);
      alert('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.imageUrl;
  };

  const searchImages = async (query = searchQuery, source = searchSource) => {
    if (!query.trim()) {
      setSearchError('Please enter a search term');
      return;
    }
    
    setSearchLoading(true);
    setSearchError('');
    setSearchResults([]);
    
    try {
      // Use our backend to search for medication images
      const response = await api.get(
        `/search-medication-images?query=${encodeURIComponent(query)}&source=${source}`
      );
      
      if (response.data.images && response.data.images.length > 0) {
        setSearchResults(response.data.images);
      } else {
        setSearchError('No images found. Try a different search term or source.');
      }
      
    } catch (err) {
      console.error('Search error:', err);
      setSearchError(err.response?.data?.error || 'Failed to search for medication images');
      
      // Fallback to mock results for demonstration
      const mockResults = generateMockMedicationImages(query);
      setSearchResults(mockResults);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Generate mock medication images for demonstration
  const generateMockMedicationImages = (query) => {
    return [
      {
        url: `https://via.placeholder.com/300x200/4CAF50/white?text=${encodeURIComponent(query + ' Package')}`,
        title: `${query} Package`,
        source: 'demo'
      },
      {
        url: `https://via.placeholder.com/300x200/2196F3/white?text=${encodeURIComponent(query + ' Box')}`,
        title: `${query} Box`,
        source: 'demo'
      },
      {
        url: `https://via.placeholder.com/300x200/FF9800/white?text=${encodeURIComponent(query + ' Tablets')}`,
        title: `${query} Tablets`,
        source: 'demo'
      },
      {
        url: `https://via.placeholder.com/300x200/E91E63/white?text=${encodeURIComponent(query + ' Medicine')}`,
        title: `${query} Medicine`,
        source: 'demo'
      }
    ];
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    searchImages(suggestion);
  };

  useEffect(() => {
    if (productName) {
      const suggestions = generateSearchSuggestions(productName);
      setSearchSuggestions(suggestions);
      setSearchQuery(productName);
      
      // Auto-search if we have a product name and it's the search tab
      if (productName && tabValue === 1) {
        searchImages(productName);
      }
    }
  }, [productName, tabValue]);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          صورة المنتج
        </Typography>
        
        <Tabs value={tabValue} onChange={(e, newVal) => setTabValue(newVal)} sx={{ mb: 2 }}>
          <Tab icon={<CloudUpload />} label="رفع صورة" />
          <Tab icon={<LocalPharmacy />} label="بحث عن أدوية" />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="image-upload"
            />
            
            {preview ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: error ? '2px solid #f44336' : '2px solid #e0e0e0'
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                  }}
                >
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                htmlFor="image-upload"
                disabled={uploading}
                sx={{
                  width: '100%',
                  height: '200px',
                  border: error ? '2px dashed #f44336' : '2px dashed #e0e0e0',
                  borderRadius: '8px',
                  flexDirection: 'column',
                  gap: 1
                }}
              >
                {uploading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <CloudUpload fontSize="large" />
                    <Typography variant="body2">
                      إضغط لرفع صورة المنتج
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PNG, JPG up to 5MB
                    </Typography>
                  </>
                )}
              </Button>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <TextField
                fullWidth
                size="small"
                placeholder="ابحث عن صور الأدوية (مثال: بنادول، فولتارين)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchImages()}
                sx={{ minWidth: 200, flexGrow: 1 }}
              />
              
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>المصدر</InputLabel>
                <Select
                  value={searchSource}
                  label="المصدر"
                  onChange={(e) => setSearchSource(e.target.value)}
                >
                  <MenuItem value={IMAGE_SOURCES.AUTO}>تلقائي (مُوصى به)</MenuItem>
                  <MenuItem value={IMAGE_SOURCES.PIXABAY}>Pixabay</MenuItem>
                  <MenuItem value={IMAGE_SOURCES.UNSPLASH}>Unsplash</MenuItem>
                  <MenuItem value={IMAGE_SOURCES.WIKIMEDIA}>Wikimedia</MenuItem>
                  <MenuItem value={IMAGE_SOURCES.FLICKR}>Flickr</MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                onClick={() => searchImages()}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />}
                sx={{ minWidth: 100 }}
              >
                {searchLoading ? 'جاري البحث...' : 'بحث'}
              </Button>
            </Box>

            {searchSuggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom color="text.secondary">
                  اقتراحات البحث:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {searchSuggestions.map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      variant="outlined"
                      size="small"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}
            
            {searchLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" my={3}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  جاري البحث عن صور الأدوية...
                </Typography>
              </Box>
            )}

            {searchResults.length > 0 && (
              <Box>
                <Typography variant="body2" gutterBottom color="text.secondary">
                  نتائج البحث ({searchResults.length} صورة):
                </Typography>
                <Grid container spacing={1}>
                  {searchResults.map((image, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover': { 
                            borderColor: 'primary.main',
                            transform: 'scale(1.02)',
                            transition: 'transform 0.2s'
                          }
                        }}
                        onClick={() => {
                          setSelectedImage(image);
                          setImageDialogOpen(true);
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.title || `نتيجة البحث ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: '120px', 
                            objectFit: 'cover' 
                          }}
                          onError={(e) => {
                            e.target.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" fill="%23f5f5f5"/><text x="60" y="60" font-family="Arial" font-size="12" fill="%23666" text-anchor="middle" dominant-baseline="middle">Image not found</text></svg>`;
                          }}
                        />
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem' }}>
                            {image.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.6rem' }}>
                            المصدر: {image.source}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {!searchLoading && searchResults.length === 0 && !searchError && (
              <Alert severity="info" sx={{ mt: 2 }}>
                أدخل اسم الدواء واضغط بحث للعثور على الصور المناسبة
              </Alert>
            )}

            <Dialog 
              open={imageDialogOpen} 
              onClose={() => setImageDialogOpen(false)} 
              maxWidth="md" 
              fullWidth
            >
              <DialogContent>
                {selectedImage && (
                  <Box>
                    <img
                      src={selectedImage.url}
                      alt={selectedImage.title}
                      style={{ 
                        width: '100%', 
                        borderRadius: '8px',
                        maxHeight: '60vh',
                        objectFit: 'contain'
                      }}
                    />
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6">
                        {selectedImage.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedImage.source && `المصدر: ${selectedImage.source}`}
                      </Typography>
                      {selectedImage.width && selectedImage.height && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          الأبعاد: {selectedImage.width} × {selectedImage.height} بكسل
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={() => setImageDialogOpen(false)} 
                  startIcon={<Close />}
                >
                  إلغاء
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => {
                    if (selectedImage) {
                      setPreview(selectedImage.url);
                      onChange(selectedImage.url);
                      setImageDialogOpen(false);
                    }
                  }}
                  startIcon={<Image />}
                >
                  استخدام هذه الصورة
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {helperText || error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageUpload;